import { useIntl } from 'react-intl';

import { OverlayTooltip } from '../../../core/components/overlay-tooltip/OverlayTooltip.js';
import { safeGet } from '../../../core/utils/safe-get.js';
import { useAxesFormatter } from '../../hooks/useAxesFormatter.js';
import { useXYChartColorScales } from '../../hooks/useXYChartColorScales.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import { useXYChartTooltip } from '../../hooks/useXYChartTooltip.js';
import { useXYChartTruncationMode } from '../../hooks/useXYChartTruncationMode.js';
import type { TooltipContent } from '../../types/state.js';
import {
  AREA_VARIANT,
  BAR_VARIANT,
  DOT_VARIANT,
  LINE_VARIANT,
  RECT_VARIANT,
} from '../../types/xy-chart-internals.js';
import { AreaSeriesTooltip } from './AreaSeriesTooltip.js';
import { BarSeriesTooltip } from './BarSeriesTooltip.js';
import { DotSeriesTooltip } from './DotSeriesTooltip.js';
import { LineSeriesTooltip } from './LineSeriesTooltip.js';
import { RectSeriesTooltip } from './RectSeriesTooltip.js';
import { getCustomTooltipPayload } from './utils/get-custom-tooltip-payload.js';

export const XYChartTooltipContent = ({
  content,
}: {
  content: TooltipContent;
}) => {
  const customTooltip = useXYChartTooltip();
  const axisIdToFormatter = useAxesFormatter();
  const intl = useIntl();
  const colorScaleMap = useXYChartColorScales();
  const { seriesToMetadata } = useXYChartData();
  const { datapointMetadataMap } = useXYChartData();
  const { truncationMode } = useXYChartTruncationMode();

  if (customTooltip?.hidden) {
    return null;
  }

  if (customTooltip?.template) {
    const customTooltipPayload = getCustomTooltipPayload(
      content,
      safeGet(seriesToMetadata, content.series),
      axisIdToFormatter,
      intl,
      colorScaleMap,
      safeGet(datapointMetadataMap, content.datapoint).colorOverride,
    );

    const customTooltipContent = customTooltip.template?.(customTooltipPayload);

    if (customTooltipContent === null) {
      return null;
    }

    if (customTooltipContent) {
      return (
        <OverlayTooltip
          template={customTooltipContent}
          truncation={truncationMode}
        ></OverlayTooltip>
      );
    }
  }

  switch (content.type) {
    case RECT_VARIANT:
      return (
        <RectSeriesTooltip
          datapoint={content.datapoint}
          series={content.series}
        />
      );
    case BAR_VARIANT:
      return (
        <BarSeriesTooltip
          datapoint={content.datapoint}
          series={content.series}
        />
      );
    case DOT_VARIANT:
      return (
        <DotSeriesTooltip
          datapoint={content.datapoint}
          series={content.series}
        />
      );
    case LINE_VARIANT:
      return (
        <LineSeriesTooltip
          datapoint={content.datapoint}
          series={content.series}
        />
      );
    case AREA_VARIANT:
      return (
        <AreaSeriesTooltip
          datapoint={content.datapoint}
          series={content.series}
        />
      );

    default:
      return null;
  }
};
